import React, { FC, useEffect } from 'react';
import { DataCard } from '@web/components/data-card';
import { useParams } from 'react-router-dom';
import { useDmsDispatch, useDmsState } from '@packages/contexts/dms';
import { useTranslation } from 'react-i18next';
import { analyticsService, dmsService } from '@web/services/singletons';
import { useAuthState } from '@packages/contexts/auth';
import { fetchCdnCampaignDocUrl, openDocumentTab } from '@web/vin-sessions/utils/open-document-tab';
import { getDisplayValue } from '@web/utils';
import { BmisBatteryBadge } from '@web/bmis/components/bmis-battery-badge';
import { StyleProp, TextStyle } from 'react-native';
import { RobotoFontFamily, Typography } from '@packages/ui/shared';
import colors from '@packages/core/styles/colors';
import { usePublicationsDispatch } from '../../stis-publications/context/publications.hooks';
import { publicationsActions } from '../../stis-publications/context/publications.state';
import { ANALYTICS_EVENTS } from '@packages/core/analytics';

export const openCampaignPublication = async (
    code: string | undefined,
    token: string,
    publicationDispatch: React.Dispatch<any>
): Promise<void> => {
    if (!code) return;
    // Open the tab synchronously (pop-up-safe) and navigate once the CDN URL resolves; the
    // resolving fetch also sets the CloudFront signed cookies before the tab loads the doc.
    const documentOpened = await openDocumentTab(() => fetchCdnCampaignDocUrl(code, token));
    if (!documentOpened) {
        publicationDispatch(
            publicationsActions.setToastMessage({
                toast: {
                    toastMessage: 'Unable to open the publication',
                    iconColor: 'white',
                    iconName: 'x-close',
                },
            })
        );
    }
};

export const CampaignSummaryCard: FC = () => {
    const { vin } = useParams<{ vin: string }>();
    const { hasLatestCampaignFetched, latestOpenCampaign, isLatestCampaignLoading, openCampaignCount } = useDmsState();
    const dispatch = useDmsDispatch();
    const publicationDispatch = usePublicationsDispatch();
    const { token } = useAuthState();
    const { t } = useTranslation();

    useEffect(() => {
        if (!vin) return;

        if (!hasLatestCampaignFetched) {
            dmsService.fetchAndSetLatestOpenCampaign(vin, dispatch);
        }
    }, [vin, latestOpenCampaign, hasLatestCampaignFetched]);

    const campaignCardData = React.useMemo(() => {
        if (
            !hasLatestCampaignFetched ||
            !latestOpenCampaign ||
            (latestOpenCampaign && Object.keys(latestOpenCampaign).length === 0)
        )
            return [];

        return [
            { label: t('Program Type'), value: getDisplayValue(latestOpenCampaign?.programType) },
            { label: t('Category'), value: getDisplayValue(latestOpenCampaign?.programCategory) },
            { label: t('Program code'), value: getDisplayValue(latestOpenCampaign?.code) },
            { value: getDisplayValue(latestOpenCampaign?.description) },
        ];
    }, [latestOpenCampaign, hasLatestCampaignFetched, t]);

    const typographyStyles = {
        fontFamily: RobotoFontFamily.Regular,
        fontWeight: '400',
        fontSize: 13,
        lineHeight: 16,
        color: colors.brandSecondary,
    } as unknown as StyleProp<TextStyle>;

    return (
        <DataCard
            footerButtonClickCallback={() => {
                analyticsService.logEvent(ANALYTICS_EVENTS.VIN_SESSION_VEHICLE_CARD_CLICKED, {
                    cardType: 'open_campaigns',
                    roNumber: latestOpenCampaign?.code,
                    moduleName: 'vin_session',
                    sourceModule: 'vin_session',
                });
            }}
            customButtonMethod={() => openCampaignPublication(latestOpenCampaign?.code, token, publicationDispatch)}
            data={campaignCardData}
            footerButtonText="View Publication"
            footerButtonLink={``}
            footerNeighbor={
                openCampaignCount > 1 ? (
                    <Typography style={typographyStyles}>{`Open campaigns: ${openCampaignCount}`}</Typography>
                ) : undefined
            }
            headerText="Latest Open Campaign"
            headerBadge={(() => {
                const s = String(latestOpenCampaign?.programStatus || '')
                    .toLowerCase()
                    .trim();
                let label = s;
                if (s.startsWith('open')) {
                    if (s.includes('limited')) label = 'open parts limited';
                    else if (s.includes('remedy')) label = 'open no remedy yet';
                    else label = 'open';
                } else if (s.includes('expired')) {
                    label = 'expired';
                } else if (s.includes('complete')) {
                    label = 'completed';
                }
                return <BmisBatteryBadge isCampaign={true} label={label} />;
            })()}
            loading={isLatestCampaignLoading}
            isEmpty={
                isLatestCampaignLoading
                    ? false
                    : Boolean(
                          !latestOpenCampaign || (latestOpenCampaign && Object.keys(latestOpenCampaign).length === 0)
                      )
            }
            emptyTitle="There is no open campaign for this vehicle"
            emptyDescription="A campaign will appear here if the vehicle has an open campaign."
        />
    );
};
